<template>
	<div class="grid-flex-container">
		<grid-history-toolbar
			:quickFilter="quickFilter"
			:advancedFilter="advancedFilter"
			:visibleColumns="visibleColumns"
			:showUsers="false"
			@quickFilterChanged="onQuickFilterChanged"
			@advancedFilterChanged="onAdvancedFilterChanged"
			@dateChanged="onDateChanged"
			@export="onExport"
			class="mb-4"
		/>
		<div class="generic-grid-container">
			<v-data-table
				dense
				fixed-header
				v-if="recordHeaders"
				:headers="recordHeaders"
				:single-expand="false"
				:items="recordsData"
				item-key="RecordId"
				:loading="loadingData"
				:expanded="records"
				:server-items-length="historyDataCount"
				:footer-props="{
					itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
					showFirstLastPage: true,
					showCurrentPage: true,
					pagination: {
						page: options.page,
						itemsPerPage: options.itemsPerPage,
						pageStart,
						pageStop,
						pageCount,
						itemsLength: historyDataCount,
					},
				}"
				:options.sync="options"
				:hide-default-footer="loadingData"
				:hide-default-header="true"
				:show-expand="true"
			>
				<template v-slot:item="{ item, headers }">
					<tr
						class="v-data-table__expanded v-data-table__expanded__row"
					>
						<td class="text-start pa-0">
							<table>
								<tr>
									<td class="text-start">
										<v-tooltip top>
											<template
												v-slot:activator="{ on, attrs }"
											>
												<v-btn
													v-bind="attrs"
													v-on="on"
													fab
													small
													icon
													elevation="1"
													class="action-btn"
													@click="
														openItemHistory(
															item.RecordId
														)
													"
												>
													<v-icon color="info" small
														>mdi-history</v-icon
													>
												</v-btn>
											</template>
											<span
												>Show all historical changes for
												this record</span
											>
										</v-tooltip>
									</td>
									<td
										v-for="(header, index) in headers"
										:key="index"
										class="text-start font-weight-medium"
									>
										<span
											v-if="header.text != ''"
											class="ml-2"
											>{{ header.text }}</span
										>
										<span v-if="header.text != ''">:</span>
										<span
											v-if="header.text != ''"
											class="pl-1 pr-2"
											>{{
												getGroupHeaderValue(
													item,
													header.value
												)
											}}</span
										>
									</td>
								</tr>
							</table>
						</td>
					</tr>
				</template>
				<template v-slot:expanded-item="{ headers, item }">
					<td :colspan="headers.length" class="ma-0 pa-0">
						<v-data-table
							v-if="expandedHeaders"
							dense
							fixed-header
							elevation="0"
							class="recordHistoryGrid"
							:headers="expandedHeaders"
							:items="getHistoryData(item)"
							:hide-default-footer="true"
						>
							<template v-slot:item.ActionDate="{ item }">{{
								getDateTimeFormat(item.ActionDate)
							}}</template>
							<template v-slot:item.IrisUserId="{ item }">{{
								getUserDisplay(item.IrisUserId)
							}}</template>
							<template v-slot:item.IrisProcess="{ item }">{{
								getIrisProcessDisplayFormat(item.IrisProcess)
							}}</template>
							<template v-slot:item.Deleted="{ item, index }">
								<span
									:class="hasChanges(item, index, 'Deleted')"
								>
									<v-icon color="error" v-if="item.Deleted"
										>mdi-close-circle</v-icon
									>
								</span>
							</template>
							<template
								v-for="column in visibleColumns"
								v-slot:[`item.${column.FieldName}`]="{
									item,
									index,
								}"
							>
								<template
									v-if="
										column.DataField.DataType ===
											fieldDataTypes.FILE &&
										item[column.FieldName + '_FileName']
									"
								>
									<span
										:key="column.FieldName"
										:class="
											hasChanges(
												item,
												index,
												column.FieldName
											)
										"
									>
										<v-icon class="mr-2" color="primary"
											>mdi-file-outline</v-icon
										>
										{{
											item[column.FieldName + '_FileName']
										}}
									</span>
								</template>
								<template
									v-else-if="
										column.DataField.DataType ===
											fieldDataTypes.DATETIME ||
										column.DataField.DataType ===
											fieldDataTypes.AUTOGENERATED_DATETIME
									"
								>
									<span
										:key="column.FieldName"
										:class="
											hasChanges(
												item,
												index,
												column.FieldName
											)
										"
									>
										{{
											getDateTimeFormat(
												item[column.FieldName]
											)
										}}
									</span>
								</template>
								<template
									v-else-if="
										column.DataField.DataType ===
										fieldDataTypes.DATE
									"
								>
									<span
										:key="column.FieldName"
										:class="
											hasChanges(
												item,
												index,
												column.FieldName
											)
										"
									>
										{{
											getDateFormat(
												item[column.FieldName]
											)
										}}
									</span>
								</template>
								<template v-else>
									<span
										:key="column.FieldName"
										:class="
											hasChanges(
												item,
												index,
												column.FieldName
											)
										"
									>
										{{ item[column.FieldName] }}
									</span>
								</template>
							</template>
						</v-data-table>
					</td>
				</template>
			</v-data-table>
		</div>
	</div>
</template>
<script>
import GridHistoryToolbar from './GridHistoryToolbar.vue';
import { mapGetters } from 'vuex';
import { GridsHistoryActions } from '../../../store/actions';
import { GridsGetters, GridsHistoryGetters } from '../../../store/getters';
import { gridViewTypes } from '../../../enums/gridViewTypes';
import projectSettingsMixin from '../../../mixins/projectSettingsMixin';
import { FieldDataTypes } from '../../../enums/fieldDataTypes';
import { processEnum } from '../../../enums/processEnum';

export default {
	name: 'GridHistoryPerRecord',
	components: {
		GridHistoryToolbar,
	},
	mixins: [projectSettingsMixin],
	data() {
		return {
			expanded: [],
			quickFilter: { FieldId: null, Value: '' },
			advancedFilter: { Operator: 1, Filters: [], FiltersCount: 0 },
			startDate: null,
			endDate: null,
			options: {
				itemsPerPage: 50,
				page: 1,
			},
		};
	},
	watch: {
		'options.itemsPerPage'() {
			this.loadData();
		},
		'options.page'() {
			this.loadData();
		},
	},
	computed: {
		fieldDataTypes() {
			return FieldDataTypes;
		},
		...mapGetters({
			entities: [GridsGetters.GET_ENTITIES],
			gridViews: [GridsGetters.GET_GRID_VIEWS],
			records: [GridsHistoryGetters.GRID_RECORDS],
			historyData: [GridsHistoryGetters.GRID_DATA],
			historyDataCount: [GridsHistoryGetters.GRID_DATA_COUNT],
			loadingData: [GridsHistoryGetters.GRID_LOADING],
			irisUsersMetadata: [GridsHistoryGetters.IRIS_USER_METADATA],
			mergeConditionsFields: [
				GridsHistoryGetters.MERGE_CONDITINOS_FIELDS,
			],
		}),

		recordsData() {
			if (this.loadingData) return [];
			return this.records;
		},

		selectedEntityName() {
			return this.$route.params.pageName;
		},
		selectedEntity() {
			if (!this.entities) return null;
			if (this.entities.length <= 0) return null;
			if (!this.selectedEntityName) return null;
			return this.entities.find((x) => x.Name == this.selectedEntityName);
		},
		selectedGridView() {
			if (!this.selectedEntity) return null;
			if (!this.selectedEntity.Id) return null;
			if (!this.gridViews) return null;
			return this.gridViews.find(
				(x) =>
					x.EntityId == this.selectedEntity.Id &&
					this.UserId == null &&
					gridViewTypes.Grids
			);
		},

		tableColumns() {
			return [
				{
					Label: 'Action date',
					FieldName: 'ActionDate',
				},
				{
					Label: 'Iris User',
					FieldName: 'IrisUserId',
				},
				...this.visibleColumns,
			];
		},
		visibleColumns() {
			if (!this.selectedGridView) return [];
			let columns = JSON.parse(
				JSON.stringify(this.selectedGridView.Columns)
			);
			return columns.sort((a, b) => a.Order - b.Order);
		},

		expandedHeaders() {
			if (!this.visibleColumns) return null;
			let result = [
				{
					text: 'Action Date',
					value: 'ActionDate',
					groupable: false,
					sortable: false,
					align: 'start',
				},
				{
					text: 'Iris User',
					value: 'IrisUserId',
					groupable: false,
					sortable: false,
					align: 'start',
				},
				{
					text: 'Process',
					value: 'IrisProcess',
					groupable: false,
					sortable: false,
					align: 'start',
				},
				{
					text: 'Deleted',
					value: 'Deleted',
					groupable: false,
					sortable: false,
					align: 'start',
				},
				...this.visibleColumns.map((x) => ({
					text: x.Label,
					value: x.FieldName,
					groupable: false,
					sortable: false,
					align: 'start',
				})),
			];
			return result;
		},

		recordHeaders() {
			let result = [
				{
					text: 'Record Id',
					value: 'RecordId',
					groupable: false,
					sortable: false,
					align: 'start',
				},
			];
			if (this.mergeConditionsFields && this.visibleColumns) {
				let gvfIds = this.mergeConditionsFields.map(
					(x) => x.GridViewFieldId
				);
				let columns = this.visibleColumns
					.filter((x) => gvfIds.includes(x.Id))
					.map((x) => ({
						text: x.Label,
						value: x.FieldName,
						groupable: false,
						sortable: false,
						align: 'start',
					}));
				result = [...result, ...columns];
			}
			return result;
		},

		totalCount() {
			return this.historyDataCount;
		},
		pageStart() {
			return this.options.itemsPerPage * (this.options.page - 1);
		},
		pageStop() {
			return this.options.itemsPerPage * this.options.page;
		},
		pageCount() {
			if (!this.options.itemsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(
				this.totalCount / this.options.itemsPerPage
			);
			let remainder = this.totalCount % this.options.itemsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},
	},

	mounted() {
		if (this.selectedEntity && this.selectedEntity.Id) {
			this.$store
				.dispatch(
					GridsHistoryActions.LOAD_MERGE_CONDITIONS,
					this.selectedEntity.Id
				)
				.then(() => {
					this.loadData();
				});
		}
	},
	destroyed() {
		this.$store.dispatch(GridsHistoryActions.CLEAR);
	},
	methods: {
		onExport(options) {
			console.log('onExport called with options:', options);
			console.log('selectedEntity:', this.selectedEntity);
			console.log(
				'Export action being dispatched:',
				GridsHistoryActions.EXPORT
			);

			let payload = {
				EntityId: this.selectedEntity.Id,
				RecordId: null,
				StartDate: this.startDate,
				EndDate: this.endDate,
				Page: this.options.page,
				RowsPerPage: this.options.itemsPerPage,
				QuickFilter: this.quickFilter,
				AdvancedFilter: this.advancedFilter,
				IncludeAllRecords: false,
				TimeZone: options.timeZone,
			};
			if (options && options.allData) {
				payload.Page = 0;
				payload.RowsPerPage = 0;
				payload.IncludeAllRecords = true;
			}
			console.log('Dispatching export with payload:', payload);
			this.$store.dispatch(GridsHistoryActions.EXPORT, {
				payload: payload,
				rawData: options.rawData,
			});
		},

		loadData() {
			let payload = {
				EntityId: this.selectedEntity.Id,
				RecordId: null,
				StartDate: this.startDate,
				EndDate: this.endDate,
				Page: this.options.page,
				RowsPerPage: this.options.itemsPerPage,
				QuickFilter: this.quickFilter,
				AdvancedFilter: this.advancedFilter,
			};
			this.$store.dispatch(GridsHistoryActions.LOAD_GRID_DATA, payload);
		},
		getHistoryData(item) {
			return this.historyData.filter((x) => x.Id == item.RecordId);
		},
		getUserDisplay(userId) {
			var user = this.irisUsersMetadata.find((x) => x.Id == userId);
			if (user) {
				return user.IrisUserFormat;
			}
			return userId;
		},
		onQuickFilterChanged(value) {
			this.quickFilter = value;
			this.loadData();
		},
		onAdvancedFilterChanged(value) {
			this.advancedFilter = value;
			this.loadData();
		},
		onDateChanged(value) {
			this.startDate = value[0];
			this.endDate = value[1];
			this.loadData();
		},
		hasChanges(item, index, column) {
			let historyData = this.historyData.filter((x) => x.Id == item.Id);
			if (historyData.length > index + 1) {
				let newColumn = historyData[index][column];
				let oldColumn = historyData[index + 1][column];
				if (newColumn !== oldColumn) {
					return 'changed pa-2';
				}
			}
			return 'pa-2';
		},
		getIrisProcessDisplayFormat(process) {
			switch (process) {
				case processEnum.DataImport:
					return 'Data Import';
				case processEnum.EditGrid:
					return 'Datasets';
				case processEnum.InventoryForm:
					return 'Inventory Form';
				default:
					return '';
			}
		},
		getGroupHeaderValue(item, fieldName) {
			if (fieldName == 'RecordId') return item[fieldName];
			let recordSubset = this.getHistoryData(item);
			if (recordSubset && recordSubset.length > 0) {
				let gvf = this.visibleColumns.find(
					(x) => x.FieldName == fieldName
				);
				if (gvf && gvf.DataField && gvf.DataField.DataType) {
					switch (gvf.DataField.DataType) {
						case FieldDataTypes.DATETIME:
						case FieldDataTypes.AUTOGENERATED_DATETIME:
							return this.getDateTimeFormat(
								recordSubset[0][fieldName]
							);
						case FieldDataTypes.USER:
							return this.getUserDisplay(
								recordSubset[0][fieldName]
							);
						default:
							return recordSubset[0][fieldName];
					}
				}
				return recordSubset[0][fieldName];
			}
			return null;
		},
		openItemHistory(id) {
			this.$router.push({
				name: 'DatasetsRecordHistory',
				params: {
					pageName: this.selectedGridView.Entity.Name,
					assetId: id,
				},
			});
		},
	},
};
</script>
<style lang="css">
.v-data-table__empty-wrapper {
	display: none;
}

.v-data-table__expanded__row {
	background: #eeeeee;
}

.v-data-table
	> .v-data-table__wrapper
	tbody
	tr.v-data-table__expanded__content {
	box-shadow: none;
}

span.changed {
	background-color: #ffc00030;
}

.recordHistoryGrid .v-data-table__wrapper {
	overflow-y: hidden;
}
</style>

<style lang="scss">
.grid-flex-container {
	.v-card__text {
		overflow-y: scroll;

		.changed {
			background-color: #ffc00030;
		}

		.grid-field {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}
		.v-input.v-input--is-readonly {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}
	}
}
</style>
